import type { GetServerSidePropsContext } from 'next'
import { useEffect } from 'react'
import dynamic from 'next/dynamic'

import { BogoBanner, BogoPromoModal } from '@mc/bogo'
import { StaticDataProvider } from '@mc/static-data-react'
import { PAGE_SOURCES, PromoExplainerModal, DiscountModal } from '@mc/promos'
import { TrackingParamsProvider } from '@mc/tracking-context'
import { TRACK_PROPS, trackPage } from '@mc/track-event'

import { trackProductViewedCourse } from 'libs/events'
import { getPageData } from 'libs/server/page-data'
import {
  getV2Categories,
  getCourse,
  getCourseMeta,
  getCoursesRelatedToCourse,
  getArticlesRelatedToCourse,
  getFreeTrialByToken,
  getDefaultProduct,
  getOutcomesKeyLearnings,
} from 'libs/server/api'
import { transformCourseRedirect } from 'libs/server/redirect'
import { getBucket } from '@mc/next-growthbook'
import { pageErrorHandler } from 'libs/server/page-error-handler'
import {
  getGrowthClassBySlug,
  getGrowthClassCategories,
  getGrowthClassesBySubcategoryIds,
  getGrowthClassSubcategories,
  getPrimaryCategoryAndSubcategoryForGrowthClass,
} from 'libs/server/api/growth'
import { useSprig } from 'libs/sprig'

import GuestPassBanner from 'components/GuestPassBanner'
import { Navigation } from 'components/Navigation'
import { StickyFooter } from 'components/StickyFooter'
import { redirect } from 'libs/utils'
import { ssrDynamicHero } from 'libs/dynamic-hero'
import { getSSRQuizOnlyOnceCookie } from 'libs/utils/quizOnlyOnce'
import { CM_OUTCOMES_EXP_FLAG } from 'experiments/pen-3644_v3'
import { GoogleOneTap } from 'experiments/pen-3951_v2_google_email_capture/components/GoogleOneTap'
import { BreadcrumbBarWrapperContextualBrowseExp } from 'experiments/pen-5015_v1_contextual_browse'
import { CMTimelineViewExperiment } from 'experiments/pen-5613_v1_cm_timeline_view'

import { trackViewedClass } from './trackViewedClass'
import Meta from './components/Meta'
import { ClassCategoryBreadcrumbBar } from './components/CategoryBreadcrumbBar'
import { getArticleCategory } from './utils/articleCategories'
import { getClassSpecialType } from './classSpecialType'
import type { CoursePageProps } from './types'

const Hero = dynamic(
  () =>
    import(/* webpackChunkName: "control-hero" */ './components/Hero').then(
      (m) => m.Hero,
    ),
  { ssr: true },
)

const BelowTheFold = dynamic(() => import('./components/BelowTheFold'), {
  ssr: false,
})

const Course = ({
  course,
  metadata,
  trial,
  primaryGrowthCatAndSubcat,
  annualPass,
  relatedArticles,
  relatedCourses,
  dynamicHero,
  savedDynamicHero,
  utmDynamic,
  ctaNextPage,
  classSpecialType,
  classSubcategories,
  otherClassesInSameSubcategories,
  outcomesKeyLearnings,
  classCategories,
  growthClass, // pen-5557_v1
}: CoursePageProps) => {
  useEffect(() => {
    if (annualPass) {
      trackProductViewedCourse({
        annualPass,
        course,
        coursePrimaryCategory: primaryGrowthCatAndSubcat.primaryCategory,
        courseCategoryIds: classCategories.map((cat) => cat.id),
        primarySubcategory: primaryGrowthCatAndSubcat?.primarySubcategory,
        courseSubcategoriesIds: classSubcategories.map((subcat) => subcat.id),
      })
    }
  }, [
    course,
    primaryGrowthCatAndSubcat,
    annualPass,
    classSubcategories,
    classCategories,
  ])

  useEffect(() => {
    trackPage('Course Marketing', {
      saved_completed_quiz: !!ctaNextPage,
      platform: 'next',
    })
  }, [ctaNextPage])

  useSprig()

  return (
    <TrackingParamsProvider value={{ [TRACK_PROPS.PAGE_TYPE]: 'next-classes' }}>
      <BogoPromoModal />

      <StaticDataProvider
        value={{
          annualPass,
          course,
          trial,
          metadata,
          primaryGrowthCatAndSubcat,
          relatedArticles,
          relatedCourses,
          dynamicHero,
          savedDynamicHero,
          utmDynamic,
          ctaNextPage,
          outcomesKeyLearnings,
          classSpecialType,
          classSubcategories,
          otherClassesInSameSubcategories,
          classCategories,
          growthClass, // pen-5557_v1
        }}
        providerKey={course.slug}
      >
        <Meta />
        {/* Modals */}
        <PromoExplainerModal pageSource={PAGE_SOURCES.CM_PAGE} />
        <DiscountModal />
        {/* Banners */}
        <BogoBanner />
        <GuestPassBanner />
        {/* Page Content */}
        <CourseContent course={course} />
        {/* Google One Tap */}
        <GoogleOneTap />
      </StaticDataProvider>
    </TrackingParamsProvider>
  )
}

const CourseContent = ({ course }: { course: CoursePageProps['course'] }) => (
  <CMTimelineViewExperiment
    Control={
      <>
        <Navigation /> {/* pen-5015_v1 experiment */}
        <BreadcrumbBarWrapperContextualBrowseExp>
          <ClassCategoryBreadcrumbBar />
        </BreadcrumbBarWrapperContextualBrowseExp>
        <Hero />
        <BelowTheFold key={course.id} />
        <StickyFooter />
      </>
    }
  />
)

Course.pageSettings = {
  showPromo: true,
  hideNavigation: true, // pen-5015_v1 experiment
  // analyticsLabel intentionally missing: trackPage is called above with extra attributes (quiz only once)
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const {
    query: { slug: slugQuery },
    req: {
      cookies: { referral_token: referralToken },
    },
    resolvedUrl,
  } = ctx
  if (typeof slugQuery !== 'string') {
    return pageErrorHandler(new Error('Invalid slug'))
  }

  let course
  try {
    course = await getCourse(ctx, { slug: slugQuery })
  } catch (err) {
    return pageErrorHandler(err)
  }

  const { slug } = course
  if (slug !== slugQuery) {
    return redirect(ctx, `/classes/${slug}`, { queryExclude: ['slug'] })
  }

  const isCMOutcomesVariant = (
    await getBucket(ctx, CM_OUTCOMES_EXP_FLAG)
  )?.includes('variant')

  trackViewedClass(ctx.req, ctx.res, slug)

  const { dynamicHero, savedDynamicHero, utmDynamic } = await ssrDynamicHero(
    ctx,
    slug,
  )

  const primaryGrowthCatAndSubcat =
    await getPrimaryCategoryAndSubcategoryForGrowthClass(ctx, slug)

  const articleCategorySlug = getArticleCategory(
    primaryGrowthCatAndSubcat?.primaryCategory?.slug || '',
    true,
  )

  const ctaNextPage = await getSSRQuizOnlyOnceCookie(ctx)

  const classSpecialType = getClassSpecialType(course)

  const classSubcategories = await getGrowthClassSubcategories(ctx, slug)

  const data = await getPageData({
    criticalPromises: {
      metadata: getCourseMeta(ctx, { slug }),
    },
    nonCriticalPromises: {
      annualPass: getDefaultProduct(ctx),
      relatedCourses: getCoursesRelatedToCourse(ctx, { slug }),
      relatedArticles: getArticlesRelatedToCourse(ctx, articleCategorySlug),
      trial: getFreeTrialByToken(ctx, { referralToken }),
      outcomesKeyLearnings: isCMOutcomesVariant
        ? getOutcomesKeyLearnings(course)
        : Promise.resolve(null),
      classCategories: getGrowthClassCategories(ctx, slug),
      otherClassesInSameSubcategories: getGrowthClassesBySubcategoryIds(
        ctx,
        classSubcategories.map((subcat) => subcat.id),
      ),
      categories: getV2Categories(ctx),
      growthClass: getGrowthClassBySlug(ctx, slug), // pen-5557_v1
    },
    propsMapper: (restProps) => ({
      course,
      dynamicHero: dynamicHero || {},
      utmDynamic: utmDynamic || {},
      savedDynamicHero,
      ctaNextPage,
      classSpecialType,
      classSubcategories,
      primaryGrowthCatAndSubcat,
      ...restProps,
    }),
  })

  return transformCourseRedirect(slug, data, resolvedUrl)
}

export default Course
